/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The pieces the team screen is drawn from. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { ROLE_COLORS } from '@/features/agency-settings-v2/constants/role-colors';
import type { TeamQuota } from '@/services/requests/housien-services/AgenciesService.team';
import { meterFill, meterTone, type MeterTone } from './team-rows';

/** What a meter's tone paints. Mustard near the limit, copper past it. */
const TONE_COLOR: Record<MeterTone, string> = {
  normal: 'var(--seed-base)',
  warning: 'var(--accent-mostarda)',
  over: 'var(--accent-cobre)',
};

export const CARD: React.CSSProperties = {
  border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)',
  background: 'var(--surface-raised)', display: 'flex', flexDirection: 'column',
};

/** A filled track. The only thing on this screen that reports a quantity. */
export const Meter: React.FC<{ fill: number | null; tone: MeterTone; height: number }> = ({
  fill, tone, height,
}) => (
  <div
    style={{
      height, borderRadius: 'var(--radius-full)',
      background: 'var(--border-ultra-subtle)', overflow: 'hidden',
    }}
  >
    <div
      style={{
        height: '100%', borderRadius: 'var(--radius-full)',
        width: `${Math.round((fill ?? 0) * 100)}%`, background: TONE_COLOR[tone],
      }}
    />
  </div>
);

/**
 * One person's allowance: what they have drawn, out of what their seat gives.
 *
 * The ceiling comes from the plan that pays for that seat, which is why this
 * is per row and not per agency.
 */
export const QuotaCell: React.FC<{ label: string; quota: TeamQuota }> = ({ label, quota }) => {
  const { t } = useTranslation('features/agency-settings');
  const tone = meterTone(quota);
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0 }}
      title={t('v2.team.quotaTooltip', {
        label, used: quota.used, total: quota.total ?? '∞',
      })}
    >
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-tag)', lineHeight: 1.2, color: 'var(--text-tertiary-soft)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Text>
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-meta)', lineHeight: 1.2, fontWeight: 'var(--font-bold)',
          color: TONE_COLOR[tone], whiteSpace: 'nowrap',
        }}
      >
        {quota.total == null
          ? String(quota.used)
          : t('v2.team.quotaValue', { used: quota.used, total: quota.total })}
      </Text>
      <Meter fill={meterFill(quota)} tone={tone} height={4} />
    </div>
  );
};

/** The round two letters that stand in for a face. */
export const Avatar: React.FC<{ initials: string; muted?: boolean }> = ({ initials, muted }) => (
  <div
    style={{
      width: 30, height: 30, borderRadius: 'var(--radius-full)', flex: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)',
      color: muted ? 'var(--text-tertiary-soft)' : 'var(--text-inverse)',
      background: muted ? 'var(--border-ultra-subtle)' : 'var(--seed-base)',
    }}
  >
    {initials}
  </div>
);

/** What somebody is allowed to do, in the colour the role system gives it. */
export const RolePill: React.FC<{ role: string | null; label: string }> = ({ role, label }) => {
  const colour = ROLE_COLORS[role ?? '']?.text ?? 'var(--text-secondary)';
  return (
    <Text
      component="span"
      style={{
        justifySelf: 'start', display: 'inline-flex', alignItems: 'center',
        gap: 'var(--space-1-5)', height: 22, padding: '0 var(--space-2-5)',
        borderRadius: 'var(--radius-full)', background: 'var(--surface-primary)',
        border: '1px solid var(--border-ultra-subtle)', fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-semibold)', color: colour, whiteSpace: 'nowrap',
      }}
    >
      <i
        aria-hidden="true"
        style={{
          width: 6, height: 6, borderRadius: 'var(--radius-full)', background: colour,
        }}
      />
      {label}
    </Text>
  );
};

/** The plan's name, as the card's badge. */
export const PlanPill: React.FC<{ label: string }> = ({ label }) => (
  <Text
    component="span"
    style={{
      display: 'inline-flex', alignItems: 'center', height: 22,
      padding: '0 var(--space-2-5)', borderRadius: 'var(--radius-full)',
      fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)',
      background: 'var(--brand-beige)', color: 'var(--seed-base)',
    }}
  >
    {label}
  </Text>
);

export const PlusIcon: React.FC = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" aria-hidden="true"
  >
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);

export const DotsIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 15 15" fill="currentColor" aria-hidden="true">
    <path d="M3.625 7.5a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0zm5 0a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0zm5 0a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0z" />
  </svg>
);

export const DriveIcon: React.FC = () => (
  <svg
    width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flex: 'none' }}
  >
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  </svg>
);

/**
 * The line that folds a card's spare seats away, and brings them back.
 *
 * It sits where the seats it hides would be, so the count reads as the rest
 * of the list rather than as a note about it.
 */
export const FreeSeatsToggle: React.FC<{
  planId: string; hidden: number; onClick: () => void;
}> = ({ planId, hidden, onClick }) => {
  const { t } = useTranslation('features/agency-settings');
  return (
    <UnstyledButton
      onClick={onClick}
      data-testid={`team-free-seats-toggle-${planId}`}
      aria-expanded={hidden === 0}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)',
        width: '100%', padding: 'var(--space-3) var(--space-5)',
        background: 'none', cursor: 'pointer', textAlign: 'left',
        fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)',
        color: 'var(--seed-base)',
      }}
    >
      {hidden > 0
        ? t('v2.team.freeSeatsMore', { count: hidden })
        : t('v2.team.freeSeatsLess')}
    </UnstyledButton>
  );
};

/** The "+ Adicionar cadeira" link on a card's header. */
export const AddSeatButton: React.FC<{ planId: string; onClick: () => void }> = ({
  planId, onClick,
}) => {
  const { t } = useTranslation('features/agency-settings');
  return (
    <UnstyledButton
      onClick={onClick}
      data-testid={`team-add-seat-${planId}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)', height: 28,
        background: 'none', cursor: 'pointer', fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-bold)', color: 'var(--seed-base)',
      }}
    >
      <PlusIcon />
      {t('v2.team.addSeat')}
    </UnstyledButton>
  );
};
